import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { toast } from 'sonner';
import { ArrowRight, Sparkles, Mail, CheckCircle2, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PhoneLoginForm } from '@/components/auth/PhoneLoginForm';
import { Logo } from '@/components/brand/Logo';
import { ThemeToggle } from '@/components/brand/ThemeToggle';
import { useAuth } from '@/hooks/use-auth';
import { clearAdminPermsCache } from '@/hooks/use-admin-perms';
import { homeForUser } from '@/lib/authRoutes';
import { consumePostAuthRedirect } from '@/lib/postAuthRedirect';
import { useTermsGate } from '@/components/auth/TermsGate';

// Public LinkedIn OAuth client id (the secret stays server-side).
const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID;

// Brand logos for the social sign-in buttons.
const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);
const LinkedInLogo = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
    <path fill="#0A66C2" d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
  </svg>
);

const stats = [
  { v: '1,240', l: 'Founders' },
  { v: '218', l: 'Mentors' },
  { v: '₹4.2 Cr', l: 'Raised by alum' },
];

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [authMode, setAuthMode] = useState('email'); // 'email' | 'phone'
  const [loading, setLoading] = useState(false);
  const [personaLoadingId, setPersonaLoadingId] = useState(null);
  const [magicSent, setMagicSent] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleLoadingId, setRoleLoadingId] = useState(null);
  const navigate = useNavigate();
  const { exchangeGoogleCode, requestMagicLink, loginAsPersona, loginAsRole, user, API } = useAuth();
  const { ensureAccepted, TermsFooter, TermsModalUi } = useTermsGate();

  // Fetch the 6 demo personas from the backend (single source of truth).
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/auth/personas`)
      .then((r) => (r.ok ? r.json() : { personas: [] }))
      .then((d) => { if (!cancelled) setPersonas(d.personas || []); })
      .catch(() => { /* silent — the persona panel just stays empty */ });
    fetch(`${API}/api/auth/preview-roles`)
      .then((r) => (r.ok ? r.json() : { roles: [] }))
      .then((d) => { if (!cancelled) setRoles(d.roles || []); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [API]);

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'popup',
    onSuccess: async ({ code }) => {
      setLoading(true);
      try {
        // popup flow → backend uses `postmessage` (default in exchangeGoogleCode)
        const data = await exchangeGoogleCode(code);
        toast.success('Welcome to Founder OS');
        navigate(consumePostAuthRedirect() || homeForUser(data?.user), { replace: true });
      } catch (e) {
        toast.error('Google sign-in failed', { description: String(e?.message || e) });
      } finally {
        setLoading(false);
      }
    },
    onError: () => toast.error('Google sign-in cancelled'),
  });
  const googleLoginGated = () => ensureAccepted(() => googleLogin());

  const linkedinLogin = () => {
    const redirectUri = window.location.origin + '/auth/linkedin';
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem('li_oauth_state', state);
    const url = `https://www.linkedin.com/oauth/v2/authorization?response_type=code`
      + `&client_id=${LINKEDIN_CLIENT_ID}`
      + `&redirect_uri=${encodeURIComponent(redirectUri)}`
      + `&state=${state}`
      + `&scope=${encodeURIComponent('openid profile email')}`;
    window.location.href = url;
  };
  const linkedinLoginGated = () => ensureAccepted(() => linkedinLogin());

  // Already signed in? Bounce to the persona's home (avoids setState-during-render warning).
  useEffect(() => {
    if (user) navigate(consumePostAuthRedirect() || homeForUser(user), { replace: true });
  }, [user, navigate]);
  if (user) return null;

  const submit = async (e) => {
    e?.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    ensureAccepted(async () => {
      setLoading(true);
      try {
        await requestMagicLink(trimmed);
        setMagicSent(true);
        toast.success('Check your inbox', { description: 'We sent you a sign-in link.' });
      } catch (err) {
        toast.error('Could not send link', { description: String(err?.message || err) });
      } finally {
        setLoading(false);
      }
    });
  };

  const pickPersona = async (p) => {
    setPersonaLoadingId(p.id);
    try {
      clearAdminPermsCache();
      const data = await loginAsPersona(p.id);
      toast.success(`Signed in as ${p.name}`);
      // navigate via the freshly-returned user (not the stale render closure)
      navigate(consumePostAuthRedirect() || homeForUser(data?.user) || p.route || '/app', { replace: true });
    } catch (err) {
      toast.error('Could not switch persona', { description: String(err?.message || err) });
    } finally {
      setPersonaLoadingId(null);
    }
  };

  const pickRole = async (r) => {
    setRoleLoadingId(r.id);
    try {
      clearAdminPermsCache();
      await loginAsRole(r.id);
      toast.success(`Previewing as ${r.label}`);
      navigate('/app/admin', { replace: true });
    } catch (err) {
      toast.error('Could not preview role', { description: String(err?.message || err) });
    } finally {
      setRoleLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="signin-page">
      {/* Top bar */}
      <header className="flex items-center justify-between px-5 lg:px-10 py-5">
        <Logo />
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            href="https://bowerschool.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            data-testid="view-programs-link"
          >
            New here? <span className="text-foreground underline underline-offset-4">View Programs</span>
          </a>
        </div>
      </header>

      <div className="grid lg:grid-cols-12 flex-1 max-w-7xl mx-auto w-full px-5 lg:px-10 gap-12 lg:gap-16 pb-10">
        {/* LEFT — Brand */}
        <section className="lg:col-span-6 min-w-0 flex flex-col justify-center order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-grotesk uppercase tracking-[0.2em] text-muted-foreground w-fit">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Cohort 02 · Open
          </div>

          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight mt-6 text-balance">
            Sign in to your<br />
            <span className="text-primary">founder OS.</span>
          </h1>

          <p className="text-muted-foreground text-base lg:text-lg mt-5 max-w-md text-pretty">
            One operating system for your venture, mentor calls, journal, copilot, and founder score. Built by Bower School of Entrepreneurship.
          </p>

          {/* Stats strip */}
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {stats.map((s) => (
              <div key={s.l} className="border-l-2 border-primary/40 pl-3">
                <div className="font-display text-3xl lg:text-4xl">{s.v}</div>
                <div className="text-[10px] font-grotesk uppercase tracking-[0.18em] text-muted-foreground mt-1">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Floating decorative mock */}
          <div className="hidden lg:block relative mt-12">
            <div className="absolute -left-4 top-4 rotate-[-3deg] rounded-2xl border border-border bg-card p-3 shadow-elevated w-64 z-10">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-sm">M</div>
                <div className="text-sm font-medium">Mosaic AI</div>
                <span className="ml-auto text-[10px] font-grotesk uppercase tracking-[0.16em] bg-info text-info-foreground rounded px-1.5 py-0.5">MVP</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary" style={{ width: '48%' }} />
              </div>
              <div className="text-xs text-muted-foreground mt-1.5">48% to Launch · +18% w/w</div>
            </div>
            <div className="absolute left-40 -top-4 rotate-[6deg] rounded-2xl bg-foreground text-background p-3 shadow-elevated w-56">
              <div className="text-[10px] font-grotesk uppercase tracking-[0.18em] text-background/60">Canopy</div>
              <div className="font-display text-4xl mt-1">842 <span className="text-xs text-info">+24</span></div>
            </div>
          </div>
        </section>

        {/* RIGHT — Sign in card + personas */}
        <section className="lg:col-span-6 min-w-0 flex flex-col justify-center order-1 lg:order-2">
          <div className="rounded-3xl border border-border bg-card p-5 sm:p-7 lg:p-9 shadow-elevated">
            <h2 className="font-display text-3xl lg:text-4xl">Welcome back.</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              {magicSent ? 'Check your inbox for a sign-in link.' : 'Sign in with email, phone or Google to continue building.'}
            </p>

            {!magicSent && (
              <div className="mt-5 grid grid-cols-2 gap-1 rounded-full bg-secondary p-1" data-testid="signin-mode-tabs">
                {[{ id: 'email', label: 'Email', Icon: Mail }, { id: 'phone', label: 'Phone', Icon: Phone }].map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setAuthMode(id)}
                    data-testid={`signin-tab-${id}`}
                    className={`flex items-center justify-center gap-2 h-9 rounded-full text-sm font-medium transition-all ${authMode === id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
            )}

            {!magicSent && authMode === 'phone' ? (
              <PhoneLoginForm onBeforeSend={ensureAccepted} termsFooter={TermsFooter} />
            ) : magicSent ? (
              <div className="mt-7 rounded-2xl border border-border bg-background p-5" data-testid="magic-link-sent">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">Link sent to {email}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Click the button in the email to sign in. The link expires in 15 minutes and works once.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setMagicSent(false); }}
                      className="mt-3 text-[11px] font-grotesk uppercase tracking-[0.18em] text-primary hover:underline"
                      data-testid="magic-link-resend"
                    >
                      Use a different email →
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-7 space-y-4">
                <div>
                  <label className="text-[11px] font-grotesk uppercase tracking-[0.18em] text-muted-foreground">Email</label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="signin-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@bowerschool.com"
                      autoComplete="email"
                      className="pl-9 h-11 bg-background"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    We&apos;ll email you a one-tap sign-in link. No password needed.
                  </p>
                </div>

                <Button
                  type="submit"
                  data-testid="signin-submit"
                  disabled={loading}
                  className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 gap-2 font-medium"
                >
                  {loading ? 'Sending link…' : <>Email me a sign-in link <ArrowRight className="h-4 w-4" /></>}
                </Button>
                {TermsFooter}
              </form>
            )}

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] font-grotesk uppercase tracking-[0.2em] text-muted-foreground">or continue with</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="h-11 rounded-full gap-2 border-border" data-testid="signin-google" disabled={loading} onClick={() => googleLoginGated()}>
                <GoogleLogo />
                {loading ? 'Signing in…' : 'Google'}
              </Button>
              <Button variant="outline" className="h-11 rounded-full gap-2 border-border" data-testid="signin-linkedin" disabled={loading} onClick={linkedinLoginGated}>
                <LinkedInLogo /> LinkedIn
              </Button>
            </div>
            {TermsModalUi}
          </div>

          {/* Persona panel — demo personas, instant login (hidden when demo data is off) */}
          {personas.length > 0 && (
          <div className="mt-6 rounded-3xl border border-accent/30 bg-accent/[0.04] p-5 sm:p-6 ai-glow" data-testid="persona-panel">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <span className="ai-chip"><Sparkles className="h-3 w-3" /> Try a demo persona</span>
                <p className="font-display text-xl mt-3 leading-tight">Skip the form. Step into the OS.</p>
                <p className="text-xs text-muted-foreground mt-1.5">One click = signed in. No password, no Google.</p>
              </div>
              <Sparkles className="h-5 w-5 text-accent shrink-0 animate-ai-pulse" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-5">
              {personas.filter((p) => ['rahul', 'priya'].includes(p.id)).map((p) => {
                const isBusy = personaLoadingId === p.id;
                const tag = p.id === 'priya'
                  ? { label: 'Admin', cls: 'bg-info text-info-foreground' }
                  : { label: 'Student', cls: 'bg-primary text-primary-foreground' };
                return (
                  <button
                    key={p.id}
                    onClick={() => pickPersona(p)}
                    disabled={!!personaLoadingId}
                    data-testid={`persona-${p.id}`}
                    className="text-left rounded-2xl border border-border bg-card p-3 hover:border-primary hover:shadow-elevated transition-all group disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-9 w-9"><AvatarImage src={p.avatar} /><AvatarFallback>{p.name[0]}</AvatarFallback></Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <span className={`inline-block mt-1 rounded-full px-2 py-0.5 text-[9px] font-grotesk uppercase tracking-[0.16em] font-semibold ${tag.cls}`} data-testid={`persona-tag-${p.id}`}>{tag.label}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2 line-clamp-2 leading-snug">{p.desc}</p>
                    <p className="text-[10px] font-grotesk uppercase tracking-[0.16em] text-primary mt-2 flex items-center gap-1">
                      {isBusy ? 'Signing in…' : 'Enter'} <ArrowRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
          )}
        </section>
      </div>

      <footer className="px-5 lg:px-10 py-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Bower School of Entrepreneurship · Hyderabad</span>
          <div className="flex items-center gap-5">
            <a href="https://bowerschool.com/privacy-policy" target="_blank" rel="noreferrer" className="hover:text-foreground">Privacy</a>
            <a href="https://bowerschool.com/terms-of-service" target="_blank" rel="noreferrer" className="hover:text-foreground">Terms</a>
            <a href="#" className="hover:text-foreground">Install app</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SignIn;
